import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { EmailBodyContentTypeDto } from '@/api/generated';
import { themeColors } from '@/config/theme/themeColors.ts';
import { formatDate } from '@/utils/formatDate.util.ts';
import { SanitizeHTML } from '@/utils/SanitzeHTML.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useReadEmail } from '@/views/emails/components/threadEmails/util/useReadEmail.util.ts';

type Props = {
  emailId: string;
  onClose: () => void;
};

export const EmailPreviewDialog: React.FC<Props> = ({ emailId, onClose }) => {
  const { t } = useTranslationWithPrefix('views.process.offer.emails.preview');
  const { email, isFetching } = useReadEmail({ emailId, isEnabled: true });

  const isLoading = isFetching || !email;

  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} open>
      <DialogTitle>{email?.subject?.trim() ?? t('noSubject')}</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Stack sx={{ alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <HeaderLine label={t('from')} value={email.from} />
              <HeaderLine label={t('to')} value={email.to.join(', ') || '—'} />
              {email.cc.length > 0 && <HeaderLine label={t('cc')} value={email.cc.join(', ')} />}
              <HeaderLine label={t('date')} value={formatDate(email.date, { withTime: true })} />
              <HeaderLine
                label={t('status')}
                value={email.isDraft ? t('statusDraft') : t('statusSent')}
              />
            </Stack>
            <Divider />
            <Box sx={{ maxHeight: '50vh', overflowY: 'auto', pr: 1 }}>
              {email.bodyContentType === EmailBodyContentTypeDto.Html ? (
                <SanitizeHTML html={email.body} />
              ) : (
                <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="body2">
                  {email.body}
                </Typography>
              )}
            </Box>
            {email.attachments.length > 0 && (
              <>
                <Divider />
                <Stack spacing={0.5}>
                  <Typography sx={{ fontWeight: 600 }} variant="body2">
                    {t('attachments')} ({email.attachments.length})
                  </Typography>
                  {email.attachments.map(att => (
                    <Stack
                      direction="row"
                      key={att.attachmentId ?? att.name}
                      spacing={0.5}
                      sx={{ alignItems: 'center' }}
                    >
                      <AttachFileIcon sx={{ color: themeColors.greyDark, fontSize: 14 }} />
                      <Typography variant="body2">{att.name}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const HeaderLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
    <Typography sx={{ color: themeColors.greyDark, minWidth: 56 }} variant="body2">
      {label}:
    </Typography>
    <Typography sx={{ wordBreak: 'break-word' }} variant="body2">
      {value}
    </Typography>
  </Stack>
);
